import React, { useState, useEffect, useMemo } from "react";
import {
  Alert,
  Grid,
  Box,
  Typography,
  Button,
  Container,
  FormHelperText,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ApiSelect from "../../../form/ApiSelect";
import PublishToggle from "../../../form/PublishToggle";
import PrefectureSelect from "../../../../shared/PrefectureSelect";
import DateRangeInput from "../../../form/DateRangeInput";
import MinMaxInput from "../../../form/MinMaxInput";
import FormTextField from "../../../form/FormTextField";
import FormMultilineField from "../../../form/FormMultilineField";
import SkillExperience from "../../../form/SkillExperience";
import OperationExperience from "../../../form/OperationExperience";
import RoleExperience from "../../../form/RoleExperience";
import FormSelect from "../../../form/FormSelect";
import SingleDateInput from "../../../form/SingleDateInput";
import { formatJpDateTime } from "../../../../utils/dateUtils";
import { buildRequestBody } from "../../../utils/buildRequestBody";
import {
  getDefaultJobFormValues,
  defaultSkill,
  defaultRole,
  initialOperationExperience,
} from "../../../config/defaultValues";

const JobsEditForm = ({ currentUser, csrfToken }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  // job取得用state
  const [job, setJob] = useState(null);
  const [formState, setFormState] = useState(null);
  const [operationExperience, setOperationExperience] = useState(
    initialOperationExperience
  );
  const [errors, setErrors] = useState([]);
  const [validationTriggered, setValidationTriggered] = useState(false);

  // employment_type の enum値と日本語ラベルを同期
  const EMPLOYMENT_TYPES = [
    { value: "full_time", label: "正社員" },
    { value: "contract_employee", label: "契約社員" },
    { value: "contractor", label: "業務委託" },
    { value: "temporary", label: "派遣社員" },
    { value: "part_time", label: "パート・アルバイト" },
    { value: "other", label: "その他" },
  ];

  // APIから求人取得
  useEffect(() => {
    fetch(`/company/jobs/${id}.json`)
      .then((res) => res.json())
      .then((data) => {
        setJob(data);
      })
      .catch(() => setErrors(["求人情報の取得に失敗しました"]));
  }, [id]);

  // 求人・ユーザー取得後、formState初期化
  useEffect(() => {
    if (job && currentUser) {
      setFormState({
        ...getDefaultJobFormValues(currentUser.id),
        ...job,
        skills: (job.skills ?? []).filter(Boolean).map((s) => ({
          id: s.id,
          skill_id: s.skill_id,
          skill_name: s.skill_name ?? "",
          experience_year: s.experience_year ?? "",
          level: s.level ?? "",
          visible_flag: s.visible_flag ?? true,
        })),
        roles: (job.roles ?? []).filter(Boolean).map((r) => ({
          id: r.id,
          role_id: r.role_id ?? null,
          experience_year: r.experience_year ?? "",
        })),
        operations: (job.operations ?? []).filter(Boolean).map((p) => ({
          id: p.id,
          operation_id: p.operation_id ?? null,
          experience_year: p.experience_year ?? "",
        })),
      });
      setOperationExperience(
        (job.operations ?? []).filter(Boolean).map((p) => ({
          id: p.id,
          operation_id: p.operation_id ?? null,
          experience_year: p.experience_year ?? "",
        }))
      );
    }
  }, [job, currentUser]);

  // operationExperience変更時もformState反映
  useEffect(() => {
    if (formState) {
      const mapped = operationExperience
        .filter((op) => op.operation_id)
        .map((op) => ({
          operation_id: op.operation_id,
          experience_year: op.experience_year || "",
        }));
      setFormState((prev) => (prev ? { ...prev, operations: mapped } : prev));
    }
  }, [operationExperience]);

  // 重複スキルを一意化
  const dedupedSkills = useMemo(() => {
    if (!formState || !formState.skills) return [];
    const seen = new Set();
    return formState.skills.filter((s) => {
      const key = s.skill_id
        ? `id:${s.skill_id}`
        : s.skill_name?.trim()
        ? `name:${s.skill_name.trim()}`
        : "";
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [formState]);

  // エラーが出たらページ上部にスクロール
  useEffect(() => {
    if (validationTriggered && errors.length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [validationTriggered, errors]);

  // 指定された日付が過去かを判定（空文字は除外）
  const isDateInPast = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr) < today;
  };

  // 終了日が開始日以前であればエラー（同日はOK）
  const isEndDateInvalid = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return false;
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    return end < start;
  };

  // 数値の最小値が最大値以上かどうかをチェック（同じは許容）
  const isMinGreaterThanMax = (minVal, maxVal) => {
    if (minVal === "" || maxVal === "") return false;
    const min = Number(minVal);
    const max = Number(maxVal);
    return min > max;
  };

  // 空文字・null・undefined を空とみなす
  const isEmpty = (value) =>
    value === "" || value === null || value === undefined;

  // 数値が空または負の値か
  const isEmptyOrNegative = (value) => {
    const num = Number(value);
    return value === "" || isNaN(num) || num < 0;
  };

  // 日付
  const isInvalidPublishStartDate =
    validationTriggered &&
    (isEmpty(formState.publish_start_date) ||
      isDateInPast(formState.publish_start_date));

  const isInvalidPublishEndDate =
    validationTriggered &&
    (isEmpty(formState.publish_end_date) ||
      isDateInPast(formState.publish_end_date) ||
      isEndDateInvalid(
        formState.publish_start_date,
        formState.publish_end_date
      ));

  const isInvalidJoiningDate =
    validationTriggered &&
    (isEmpty(formState.joining_date) || isDateInPast(formState.joining_date));

  // 必須入力
  const isInvalidProjectManager =
    validationTriggered && isEmpty(formState.job_manager_id);
  const isInvalidProjectName = validationTriggered && isEmpty(formState.name);
  const isInvalidProjectDescription =
    validationTriggered && isEmpty(formState.description);
  const isInvalidProjectTitle = validationTriggered && isEmpty(formState.title);
  const isInvalidEmploymentType =
    validationTriggered && isEmpty(formState.employment_type);
  const isInvalidInterviewCount =
    validationTriggered && isEmpty(formState.interview_count);
  const isInvalidPrefecture =
    validationTriggered && formState.prefecture_ids.length === 0;

  // スキルのチェック
  const isInvalidSkill =
    validationTriggered &&
    (() => {
      const validSkills = dedupedSkills.filter((skill) =>
        skill.skill_name?.trim()
      );

      // 有効なスキルが1つもない場合
      if (validSkills.length === 0) return true;

      // 重複チェック
      const names = validSkills.map((s) => s.skill_name.trim());
      return new Set(names).size !== names.length;
    })();

  // 金額
  const isInvalidPriceMin =
    validationTriggered &&
    (isEmpty(formState.price_min) || isEmptyOrNegative(formState.price_min));

  const isInvalidPriceMax =
    validationTriggered &&
    (isEmpty(formState.price_max) ||
      isEmptyOrNegative(formState.price_max) ||
      isMinGreaterThanMax(formState.price_min, formState.price_max));

  // バリデーション
  const validateForm = () => {
    const newErrors = [];

    const rules = [
      {
        field: "job_manager_id",
        isError: isEmpty(formState.job_manager_id),
        message: "担当者は入力必須です。",
      },
      {
        field: "publish_start_date",
        isError: isEmpty(formState.publish_start_date),
        message: "掲載期間の開始日は入力必須です。",
      },
      {
        field: "publish_end_date",
        isError: isEmpty(formState.publish_end_date),
        message: "掲載期間の終了日は入力必須です。",
      },
      {
        field: "publish_start_date",
        isError:
          !isEmpty(formState.publish_start_date) &&
          isDateInPast(formState.publish_start_date),
        message: "掲載期間の開始日は今日以降の日付を指定してください。",
      },
      {
        field: "publish_end_date",
        isError:
          !isEmpty(formState.publish_end_date) &&
          isDateInPast(formState.publish_end_date),
        message: "掲載期間の終了日は今日以降の日付を指定してください。",
      },
      {
        field: "publish_end_date",
        isError:
          !isEmpty(formState.publish_start_date) &&
          !isEmpty(formState.publish_end_date) &&
          isEndDateInvalid(
            formState.publish_start_date,
            formState.publish_end_date
          ),
        message: "掲載期間の終了日は掲載期間の開始日以降に設定してください。",
      },
      {
        field: "name",
        isError: isEmpty(formState.name),
        message: "求人名は入力必須です。",
      },
      {
        field: "description",
        isError: isEmpty(formState.description),
        message: "求人概要は入力必須です。",
      },
      {
        field: "title",
        isError: isEmpty(formState.title),
        message: "提案用タイトルは入力必須です。",
      },
      {
        field: "employment_type",
        isError: isEmpty(formState.employment_type),
        message: "雇用形態は入力必須です。",
      },
      {
        field: "joining_date",
        isError: isEmpty(formState.joining_date),
        message: "入社日は入力必須です。",
      },
      {
        field: "joining_date",
        isError:
          !isEmpty(formState.joining_date) &&
          isDateInPast(formState.joining_date),
        message: "入社日は今日以降の日付を指定してください。",
      },
      {
        field: "price_min",
        isError: isEmptyOrNegative(formState.price_min),
        message: "年収の下限は0以上の数値を入力してください。",
      },
      {
        field: "price_max",
        isError: isEmptyOrNegative(formState.price_max),
        message: "年収の上限は0以上の数値を入力してください。",
      },
      {
        field: "price_max",
        isError:
          !isEmpty(formState.price_min) &&
          !isEmpty(formState.price_max) &&
          isMinGreaterThanMax(formState.price_min, formState.price_max),
        message: "年収の上限は下限以上にしてください。",
      },
      {
        field: "prefecture_ids",
        isError: formState.prefecture_ids.length === 0,
        message: "勤務地エリアは最低1つ選択してください。",
      },
      {
        field: "skills",
        isError: (() => {
          const filledSkills = dedupedSkills.filter((skill) =>
            skill.skill_name?.trim()
          );
          return filledSkills.length === 0;
        })(),
        message: "スキルは1つ以上入力してください。",
      },
      {
        field: "skills",
        isError: (() => {
          const names = dedupedSkills
            .map((s) => s.skill_name?.trim())
            .filter(Boolean); // 空は除外
          const unique = new Set(names);
          return names.length !== unique.size;
        })(),
        message: "同じスキル名を複数入力することはできません。",
      },
      {
        field: "interview_count",
        isError: isEmpty(formState.interview_count),
        message: "面談回数は入力必須です。",
      },
    ];

    rules.forEach((rule) => {
      if (rule.isError) {
        newErrors.push(rule.message);
      }
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // スキルの重複チェック
  const duplicateSkillNames = useMemo(() => {
    if (!formState || !formState.skills) return [];
    const names = formState.skills
      .map((s) => s.skill_name?.trim())
      .filter(Boolean);
    return names.filter((name, i, arr) => arr.indexOf(name) !== i);
  }, [formState]);

  // useNavigate を使ったSPA送信
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setValidationTriggered(true);
      return;
    }

    if (!job || !job.id) {
      setErrors(["プロジェクト情報が正しくありません。"]);
      return;
    }

    // 送信用のbodyをAPI用JSON形式に変換
    const body = buildRequestBody(formState, job, "job");

    try {
      const res = await fetch(`/company/jobs/${job.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        // SPA遷移で詳細へ
        navigate(`/company/jobs/${job.id}`, {
          state: { flashMessage: "求人を更新しました" },
        });
      } else {
        const data = await res.json();
        setErrors(data.errors || ["保存に失敗しました。"]);
        setValidationTriggered(true);
      }
    } catch (error) {
      setErrors(["通信エラーが発生しました"]);
      setValidationTriggered(true);
    }
  };

  // フォームの各フィールドの値が変更されたときに呼び出される
  const handleChange = (e) => {
    setValidationTriggered(false);
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  // 求人がまだ来ていない間はローディング
  if (!job || !formState || !currentUser) {
    return (
      <Box p={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ position: "relative", mt: 2, mb: 10 }}>
      <Button
        onClick={() => window.history.back()}
        size="small"
        sx={{
          mb: 0.5,
          color: "#000",
          fontWeight: "bold",
          textTransform: "none",
          "&:hover": { opacity: 0.7 },
        }}
      >
        ← 戻る
      </Button>

      <form id="job-form" onSubmit={handleSubmit}>
        <Box sx={{ bgcolor: "#fff", p: 4, borderRadius: 2, boxShadow: 1 }}>
          {/* エラーの表示 */}
          {validationTriggered && errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.map((error, idx) => (
                <div key={idx}>{error}</div>
              ))}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              求人編集
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                mt: 1,
                display: "block",
                borderBottom: "1px solid #ccc",
                pb: 1,
              }}
            >
              更新日時：{formatJpDateTime(job.updated_at)}
            </Typography>
          </Box>

          <Grid container spacing={1}>
            {/* 担当者（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <ApiSelect
                value={formState.job_manager_id}
                onChange={(val) =>
                  setFormState({ ...formState, job_manager_id: val })
                }
                isRequired={true}
                endpoint="/company/users.json"
                label="求人の担当者"
                currentUserId={currentUser.id}
                error={isInvalidProjectManager}
                blankLabel="担当者を選択"
              />
            </Grid>

            {/* 掲載期間（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <DateRangeInput
                label="掲載期間"
                nameStart="publish_start_date"
                nameEnd="publish_end_date"
                valueStart={formState.publish_start_date}
                valueEnd={formState.publish_end_date}
                onChange={handleChange}
                isRequired={true}
                errorStart={isInvalidPublishStartDate}
                errorEnd={isInvalidPublishEndDate}
              />
            </Grid>

            {/* 求人名（共通コンポーネント） */}
            <Grid item xs={12}>
              <FormTextField
                label="求人名（最大50文字まで）"
                name="name"
                value={formState.name}
                onChange={handleChange}
                placeholder="業務プロセス改善支援システム"
                isRequired={true}
                maxLength={50}
                error={isInvalidProjectName}
              />
            </Grid>

            {/* 求人概要（共通コンポーネント） */}
            <Grid item xs={12}>
              <FormMultilineField
                label="求人概要（最大1000文字まで）"
                rows={10}
                name="description"
                value={formState.description}
                onChange={handleChange}
                placeholder="クライアントの業務DXを支援するため、Vue.js + Laravel を用いた社内管理システムを新規開発。要件定義～リリースまでを5名チームで実施。"
                isRequired={true}
                maxLength={1000}
                error={isInvalidProjectDescription}
              />
            </Grid>

            {/* 提案用タイトル（共通コンポーネント） */}
            <Grid item xs={12}>
              <FormTextField
                label="提案用タイトル（最大70文字まで）"
                name="title"
                value={formState.title}
                onChange={handleChange}
                placeholder="【要件定義〜開発】Vue.js×Laravel｜業務効率化Webシステム開発／フルリモートOK・年収応相談"
                isRequired={true}
                maxLength={70}
                error={isInvalidProjectTitle}
              />
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2, pl: 1 }}>
              {/* 雇用形態（左半分） */}
              <Grid item xs={6}>
                <FormSelect
                  label="雇用形態"
                  options={EMPLOYMENT_TYPES}
                  value={formState.employment_type}
                  onChange={(val) =>
                    setFormState({ ...formState, employment_type: val })
                  }
                  isRequired={true}
                  error={isInvalidEmploymentType}
                  placeholder="雇用形態を選択"
                  fullWidth
                />
              </Grid>

              {/* 入社日＋以降（右半分） */}
              <Grid item xs={6}>
                <Box sx={{ position: "relative", height: "72px" }}>
                  <SingleDateInput
                    label="入社日"
                    name="joining_date"
                    value={formState.joining_date}
                    onChange={handleChange}
                    isRequired={true}
                    error={isInvalidJoiningDate}
                    fullWidth
                  />
                  <FormHelperText
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 0,
                      fontSize: 14,
                      color: "text.secondary",
                      whiteSpace: "nowrap",
                      pr: 1,
                    }}
                  >
                    以降
                  </FormHelperText>
                </Box>
              </Grid>
            </Grid>

            {/* 金額（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <MinMaxInput
                label="年収"
                nameMin="price_min"
                nameMax="price_max"
                valueMin={formState.price_min}
                valueMax={formState.price_max}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 6) handleChange(e);
                }}
                placeholderMin="例：30"
                placeholderMax="例：50"
                unit="万円"
                isRequired={true}
                errorMin={isInvalidPriceMin}
                errorMax={isInvalidPriceMax}
              />
            </Grid>

            {/* 都道府県（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <PrefectureSelect
                value={formState.prefecture_ids}
                onChange={(ids) =>
                  setFormState((prev) => ({ ...prev, prefecture_ids: ids }))
                }
                label="勤務地エリア"
                max={3}
                isRequired={true}
                error={isInvalidPrefecture}
              />
            </Grid>

            {/* スキル（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <SkillExperience
                skills={formState.skills}
                onChange={(updated) =>
                  setFormState({ ...formState, skills: updated })
                }
                onAdd={() =>
                  setFormState({
                    ...formState,
                    skills: [...formState.skills, { ...defaultSkill }],
                  })
                }
                onRemove={(index) =>
                  setFormState({
                    ...formState,
                    skills: formState.skills.filter((_, i) => i !== index),
                  })
                }
                isRequired={true}
                error={isInvalidSkill}
                duplicateSkillNames={duplicateSkillNames}
              />
            </Grid>

            <Grid item xs={12}>
              <FormMultilineField
                label="スキル詳細（最大500文字まで）"
                rows={5}
                name="skill_detail"
                value={formState.skill_detail}
                onChange={handleChange}
                placeholder="スキルについての詳細があれば記載"
                maxLength={500}
              />
            </Grid>

            {/* 工程（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <OperationExperience
                value={operationExperience}
                onChange={setOperationExperience}
                isRequired={false}
              />
            </Grid>

            <Grid item xs={12}>
              <FormMultilineField
                label="工程詳細（最大500文字まで）"
                rows={5}
                name="operation_detail"
                value={formState.operation_detail}
                onChange={handleChange}
                placeholder="工程についての詳細があれば記載"
                maxLength={500}
              />
            </Grid>

            {/* 役割（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <RoleExperience
                roles={formState.roles}
                onChange={(updated) =>
                  setFormState({ ...formState, roles: updated })
                }
                onAdd={() =>
                  setFormState({
                    ...formState,
                    roles: [...formState.roles, { ...defaultRole }],
                  })
                }
                onRemove={(index) =>
                  setFormState({
                    ...formState,
                    roles: formState.roles.filter((_, i) => i !== index),
                  })
                }
                isRequired={false}
              />
            </Grid>

            <Grid item xs={12}>
              <FormMultilineField
                label="ポジション詳細（最大500文字まで）"
                rows={5}
                name="role_detail"
                value={formState.role_detail}
                onChange={handleChange}
                placeholder="ポジションについての詳細があれば記載"
                maxLength={500}
              />
            </Grid>

            {/* 面談回数（共通コンポーネント） */}
            <Grid item xs={12} sx={{ mb: 2 }}>
              <FormTextField
                label="面談回数"
                name="interview_count"
                value={formState.interview_count}
                onChange={handleChange}
                placeholder="1~2回"
                isRequired={true}
                error={isInvalidInterviewCount}
              />
            </Grid>

            {/* 備考（共通コンポーネント） */}
            <Grid item xs={12}>
              <FormMultilineField
                label="備考（最大1000文字まで）"
                rows={10}
                name="remark"
                value={formState.remark}
                onChange={handleChange}
                placeholder="補足事項や特記事項をご記入ください"
                isRequired={false}
                maxLength={1000}
              />
            </Grid>

            {/* 公開フラグ（共通コンポーネント） */}
            <Grid item xs={12}>
              <PublishToggle
                label="公開設定"
                value={formState.published_flag}
                onChange={(val) =>
                  setFormState({ ...formState, published_flag: val })
                }
                publishEnd={formState.publish_end_date}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              position: "absolute",
              bottom: -60,
              right: 16,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate(`/company/jobs/${job.id}`)}
              sx={{
                backgroundColor: "#ffffff",
                color: "#000",
                borderRadius: 9999,
                px: 4,
                fontWeight: "bold",
                border: "1px solid #9ca3af",
                "&:hover": {
                  opacity: 0.9,
                  backgroundColor: "#f3f4f6",
                  borderColor: "#9ca3af",
                },
                textAlign: "center",
              }}
            >
              キャンセル
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="inherit"
              sx={{
                borderRadius: 4,
                color: "#fff",
                bgcolor: "#000",
                "&:hover": { bgcolor: "#333" },
              }}
            >
              更新
            </Button>
          </Box>
        </Box>
      </form>
    </Container>
  );
};

export default JobsEditForm;
